import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon as PendingIcon,
  ChartBarIcon,
  UserGroupIcon,
  DocumentTextIcon,
  StarIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  BriefcaseIcon
} from '@heroicons/react/24/outline';
import { AXIOS_INSTANCE } from '../../services/api-client';
import { useAuth } from '../../contexts/AuthContext';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Chip,
  ChipGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalHeader,
  ModalTitle,
  Page,
  PageBody,
  PageHeader,
  Panel,
  PanelBody,
  PanelDescription,
  PanelFooter,
  PanelHeader,
  PanelTitle,
  SimplePagination,
  Spinner,
} from '../../components/ui';

// TODO: Replace with Orval-generated hooks once OpenAPI spec is updated
interface Analysis {
  analysis_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  created_at: string;
  started_at?: string;
  completed_at?: string;
  job_description_preview?: string;
  candidate_count: number;
  top_candidate_fit_score?: number;
  average_fit_score?: number;
  error_message?: string;
  version: string;
}

interface AnalysesResponse {
  analyses: Analysis[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

interface CandidateScore {
  candidate_id: string;
  full_name?: string;
  overall_score: number;
  dimensions: {
    name: string;
    score: number;
    rationale: string;
  }[];
}

interface FullAnalysisResult {
  analysis_id: string;
  status: string;
  job_description: string;
  ideal_candidate_description: string;
  diagnostic_report?: any;
  baseline_profile?: any;
  synthesis?: any;
  applicant_results: CandidateScore[];
  market_results: CandidateScore[];
  top_overall: CandidateScore[];
  patterns?: any[];
  created_at: string;
  completed_at?: string;
  error_message?: string;
}

// Layout constants matching BI page
const MIN_LIST_WIDTH = 240;
const MAX_LIST_WIDTH = 420;
const MIN_DETAIL_WIDTH = 400;

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
const normalizeScoreToPercent = (value?: number | null) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return 0;
  const percentValue = value >= 0 && value <= 1 ? value * 100 : value;
  return clamp(percentValue, 0, 100);
};

const formatScorePercent = (value?: number | null, digits: number = 0) =>
  `${normalizeScoreToPercent(value).toFixed(digits)}%`;

export const TalentAnalysisHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasAnyPermission } = useAuth();
  const splitRef = useRef<HTMLDivElement>(null);

  // Get analysis_id from URL query parameter
  const initialAnalysisIdRef = useRef(new URLSearchParams(window.location.search).get('analysis_id'));
  const initialAnalysisId = initialAnalysisIdRef.current;

  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [analyses, setAnalyses] = useState<Analysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [selectedAnalysisId, setSelectedAnalysisId] = useState<string | null>(initialAnalysisId);

  // Full analysis details
  const [fullAnalysis, setFullAnalysis] = useState<FullAnalysisResult | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Modal state for job description
  const [showJobDescriptionModal, setShowJobDescriptionModal] = useState(false);
  const [openStages, setOpenStages] = useState<string[]>([
    'stage-4-applicant-scoring',
    'stage-5-6-market-search',
  ]);

  // Split pane state
  const [listWidth, setListWidth] = useState(320);
  const [isDragging, setIsDragging] = useState(false);

  // Fetch analyses
  useEffect(() => {
    const fetchAnalyses = async () => {
      setLoading(true);
      try {
        const statusParam = selectedStatus ? `&status=${selectedStatus}` : '';
        const response = await AXIOS_INSTANCE.get(
          `/api/v1/ml-talent/analyses?page=${currentPage}&page_size=20${statusParam}`
        );

        const data: AnalysesResponse = response.data;
        setAnalyses(data.analyses);
        setTotal(data.total);
        setPages(data.pages);

        // Auto-select analysis from URL or first analysis if none selected
        setSelectedAnalysisId((currentSelectedAnalysisId) => {
          if (currentSelectedAnalysisId || data.analyses.length === 0) {
            return currentSelectedAnalysisId;
          }

          if (
            initialAnalysisIdRef.current &&
            data.analyses.some((analysis) => analysis.analysis_id === initialAnalysisIdRef.current)
          ) {
            return initialAnalysisIdRef.current;
          }

          if (initialAnalysisIdRef.current) {
            return initialAnalysisIdRef.current;
          }

          return data.analyses[0].analysis_id;
        });
      } catch (error: any) {
        console.error('Failed to fetch analyses:', error);
        // Log more details about the error
        if (error.response) {
          console.error(`API request failed: ${error.response.status} ${error.response.statusText}`);
          console.error('Response data:', error.response.data);
        } else if (error.message) {
          console.error('Error message:', error.message);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyses();
  }, [currentPage, selectedStatus]);

  // Fetch full analysis details when selected
  useEffect(() => {
    if (!selectedAnalysisId) {
      setFullAnalysis(null);
      return;
    }

    const fetchAnalysisDetails = async () => {
      setLoadingDetails(true);
      try {
        const response = await AXIOS_INSTANCE.get(
          `/api/v1/ml-talent/analysis/${selectedAnalysisId}`
        );

        const data: FullAnalysisResult = response.data;
        setFullAnalysis(data);
      } catch (error: any) {
        console.error('Failed to fetch analysis details:', error);
        if (error.response) {
          console.error(`API request failed: ${error.response.status} ${error.response.statusText}`);
          console.error('Response data:', error.response.data);
        }
      } finally {
        setLoadingDetails(false);
      }
    };

    fetchAnalysisDetails();
  }, [selectedAnalysisId]);

  // Handle drag resize
  useLayoutEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (event: MouseEvent) => {
      const rect = splitRef.current?.getBoundingClientRect();
      if (!rect) return;
      const maxLeft = rect.width - MIN_DETAIL_WIDTH;
      const proposed = event.clientX - rect.left;
      const clampedLeft = clamp(proposed, MIN_LIST_WIDTH, Math.max(MIN_LIST_WIDTH, maxLeft));
      setListWidth(clampedLeft);
    };
    const handleMouseUp = () => setIsDragging(false);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-emerald-500" />;
      case 'failed':
        return <XCircleIcon className="h-5 w-5 text-rose-500" />;
      case 'processing':
        return <ClockIcon className="h-5 w-5 animate-spin text-amber-500" />;
      default:
        return <PendingIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />;
    }
  };

  const getStatusBadgeVariant = (status: string): 'success' | 'danger' | 'warning' | 'secondary' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'danger';
      case 'processing':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const canCreateAnalysis = hasAnyPermission(['recruiter:config:read', 'platform:admin']);

  const openStage = (stageId: string) => {
    setOpenStages((currentStages) =>
      currentStages.includes(stageId) ? currentStages : [...currentStages, stageId]
    );

    window.setTimeout(() => {
      document.getElementById(stageId)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }, 0);
  };

  const renderStagePlaceholder = (
    status: string,
    messages: { processing: string; failed: string; empty: string }
  ) => {
    if (status === 'processing') {
      return (
        <Alert variant="info" title="Analysis in progress">
          {messages.processing}
        </Alert>
      );
    }

    if (status === 'failed') {
      return (
        <Alert variant="error" title="Stage did not complete">
          {messages.failed}
        </Alert>
      );
    }

    return (
      <Alert variant="neutral" title="No data available">
        {messages.empty}
      </Alert>
    );
  };

  return (
    <Page layout="full-width">
      <PageHeader
        title="Talent Search History"
        description={`${total} searches • Review past talent analyses and their results`}
        actions={
          canCreateAnalysis ? (
            <Button onClick={() => navigate('/talent/analysis-config')}>
              <SparklesIcon className="w-4 h-4" />
              New Analysis
            </Button>
          ) : null
        }
      />
      <PageBody fill padded={false}>
        <div className="shrink-0 border-b border-gray-200 bg-gray-50 px-8 py-3 dark:border-dark-border dark:bg-dark-bg">
          <div className="flex items-center gap-3">
            <span className="shrink-0 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Filter
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <Chip
                variant="pill"
                selected={selectedStatus === null}
                showIcon={false}
                onClick={() => setSelectedStatus(null)}
              >
                All ({total})
              </Chip>
              <Chip
                variant="pill"
                selected={selectedStatus === 'completed'}
                showIcon={false}
                onClick={() => setSelectedStatus('completed')}
              >
                <span className="flex items-center gap-1.5">
                  <CheckCircleIcon className="w-3.5 h-3.5" />
                  Completed
                </span>
              </Chip>
              <Chip
                variant="pill"
                selected={selectedStatus === 'processing'}
                showIcon={false}
                onClick={() => setSelectedStatus('processing')}
              >
                <span className="flex items-center gap-1.5">
                  <ClockIcon className="w-3.5 h-3.5" />
                  Processing
                </span>
              </Chip>
              <Chip
                variant="pill"
                selected={selectedStatus === 'failed'}
                showIcon={false}
                onClick={() => setSelectedStatus('failed')}
              >
                <span className="flex items-center gap-1.5">
                  <XCircleIcon className="w-3.5 h-3.5" />
                  Failed
                </span>
              </Chip>
            </div>
          </div>
        </div>

        {/* Split Pane View */}
        <div
          ref={splitRef}
          className="flex flex-1 min-h-0 bg-white dark:bg-dark-surface"
        >
          {/* Analysis List */}
          <Panel
            className="h-full flex flex-col rounded-none border-y-0 border-l-0"
            style={{ width: clamp(listWidth, MIN_LIST_WIDTH, MAX_LIST_WIDTH) }}
          >
            {loading ? (
              <PanelBody className="flex h-64 items-center justify-center">
                <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                  <Spinner size="md" />
                  Loading analyses...
                </div>
              </PanelBody>
            ) : analyses.length === 0 ? (
              <PanelBody className="flex h-64 flex-col items-center justify-center px-4 text-center">
                <ClockIcon className="mb-3 h-12 w-12 text-gray-400 dark:text-gray-500" />
                <h3 className="mb-1 text-sm font-medium text-charcoal dark:text-gray-100">No analyses yet</h3>
                <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">Start your first analysis</p>
                {canCreateAnalysis && (
                  <Button
                    size="sm"
                    onClick={() => navigate('/talent/analysis-config?new=true')}
                  >
                    Create Analysis
                  </Button>
                )}
              </PanelBody>
            ) : (
              <PanelBody className="flex-1 overflow-y-auto p-0">
                {analyses.map((analysis) => (
                  <div
                    key={analysis.analysis_id}
                    onClick={() => setSelectedAnalysisId(analysis.analysis_id)}
                    className={`cursor-pointer border-b border-gray-200 px-4 py-4 transition-colors dark:border-dark-border ${
                      selectedAnalysisId === analysis.analysis_id
                        ? 'border-l-2 border-l-eliza-red bg-gray-50 dark:bg-dark-surface-2'
                        : 'hover:bg-gray-50 dark:hover:bg-dark-surface-2/60'
                    }`}
                  >
                    <div className="flex items-start gap-2 mb-2">
                      {getStatusIcon(analysis.status)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant={getStatusBadgeVariant(analysis.status)} className="uppercase tracking-wide">
                            {analysis.status}
                          </Badge>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(analysis.created_at)}
                        </div>
                      </div>
                    </div>
                    {analysis.job_description_preview && (
                      <div className="mt-2 line-clamp-2 text-xs text-gray-500 dark:text-gray-400">
                        {analysis.job_description_preview}
                      </div>
                    )}
                    {analysis.status === 'completed' && (
                      <div className="mt-2 text-xs font-medium text-charcoal dark:text-gray-100">
                        {analysis.candidate_count} candidates
                      </div>
                    )}
                  </div>
                ))}
              </PanelBody>
            )}

            {/* Pagination */}
            {!loading && analyses.length > 0 && pages > 1 && (
              <PanelFooter className="mt-auto">
                <SimplePagination
                  page={currentPage}
                  totalPages={pages}
                  onPageChange={setCurrentPage}
                />
              </PanelFooter>
            )}
          </Panel>

          {/* Divider */}
          <div
            className="relative z-20 -mx-px w-px cursor-col-resize bg-gray-200 dark:bg-dark-border"
            onMouseDown={() => setIsDragging(true)}
            title="Drag to resize"
          />

          {/* Detail Pane */}
          <div className="flex-1 min-h-0 overflow-y-auto bg-white dark:bg-dark-surface">
            {loadingDetails ? (
              <div className="flex h-full items-center justify-center">
                <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                  <Spinner size="md" />
                  Loading details...
                </div>
              </div>
            ) : fullAnalysis ? (
              <div className="p-6">
                <div className="mx-auto max-w-5xl space-y-6">
                  {/* Top 3 Candidates Summary */}
                  {fullAnalysis.status === 'completed' && fullAnalysis.top_overall && fullAnalysis.top_overall.length > 0 && (
                    <Card className="border-eliza-red/20 bg-gradient-to-br from-eliza-red/10 via-white to-white dark:from-eliza-red/10 dark:via-dark-surface dark:to-dark-surface">
                      <CardHeader className="pb-4">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <CardTitle className="flex items-center gap-2 text-xl">
                              <StarIcon className="h-5 w-5 text-eliza-red" />
                              Top Candidates
                            </CardTitle>
                            <CardDescription>
                              Highest-ranking matches surfaced by the completed analysis.
                            </CardDescription>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openStage('stage-7-synthesis')}
                          >
                            View Full Analysis
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {fullAnalysis.top_overall.slice(0, 3).map((candidate, idx) => (
                          <Panel
                            key={candidate.candidate_id}
                            className="border-gray-200/80 bg-white/80 dark:border-dark-border/50 dark:bg-dark-surface-2/80"
                          >
                            <PanelBody className="p-4">
                              <div className="mb-2 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-eliza-red/10 text-sm font-bold text-eliza-red">
                                    #{idx + 1}
                                  </div>
                                  <div>
                                    <div className="font-medium text-charcoal dark:text-gray-100">
                                      {candidate.full_name || candidate.candidate_id}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">Overall Score</div>
                                  </div>
                                </div>
                                <div className="text-2xl font-bold text-eliza-red">
                                  {formatScorePercent(candidate.overall_score, 1)}
                                </div>
                              </div>
                              {candidate.dimensions && candidate.dimensions.length > 0 && (
                                <div className="mt-3 space-y-2">
                                  {candidate.dimensions.slice(0, 3).map((dim) => (
                                    <div key={dim.name} className="flex items-center gap-2">
                                      <span className="w-32 truncate text-xs text-gray-500 dark:text-gray-400">
                                        {dim.name}
                                      </span>
                                      <div className="h-1.5 flex-1 rounded-full bg-gray-200 dark:bg-dark-border/70">
                                        <div
                                          className="h-1.5 rounded-full bg-eliza-red"
                                          style={{ width: formatScorePercent(dim.score) }}
                                        />
                                      </div>
                                      <span className="w-12 text-right text-xs font-medium text-charcoal dark:text-gray-100">
                                        {formatScorePercent(dim.score)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </PanelBody>
                          </Panel>
                        ))}
                      </CardContent>
                    </Card>
                  )}

                  {/* Summary Statistics */}
                  <Panel>
                    <PanelHeader>
                      <PanelTitle>Analysis Summary</PanelTitle>
                      <PanelDescription>Quick metrics for the selected analysis run.</PanelDescription>
                    </PanelHeader>
                    <PanelBody>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-dark-border/50 dark:bg-dark-surface-2">
                          <div className="mb-2 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Status</div>
                          <Badge variant={getStatusBadgeVariant(fullAnalysis.status)} className="uppercase tracking-wide">
                            {fullAnalysis.status}
                          </Badge>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-dark-border/50 dark:bg-dark-surface-2">
                          <div className="mb-1 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Applicants</div>
                          <div className="text-lg font-bold text-charcoal dark:text-gray-100">
                            {fullAnalysis.applicant_results?.length || 0}
                          </div>
                          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">candidates</div>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-dark-border/50 dark:bg-dark-surface-2">
                          <div className="mb-1 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Market Search</div>
                          <div className="text-lg font-bold text-charcoal dark:text-gray-100">
                            {fullAnalysis.market_results?.length || 0}
                          </div>
                          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">candidates</div>
                        </div>
                      </div>
                    </PanelBody>
                  </Panel>

                  {/* Configuration Inputs */}
                  <Panel>
                    <PanelHeader>
                      <PanelTitle>Analysis Configuration</PanelTitle>
                      <PanelDescription>Inputs and timestamps that shaped this analysis.</PanelDescription>
                    </PanelHeader>
                    <PanelBody className="space-y-4">
                      {fullAnalysis.job_description && (
                        <div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowJobDescriptionModal(true)}
                            className="px-0 text-eliza-red hover:bg-transparent hover:text-eliza-red/80 dark:text-eliza-red"
                          >
                            <BriefcaseIcon className="h-4 w-4" />
                            View Job Description
                          </Button>
                        </div>
                      )}

                      {fullAnalysis.ideal_candidate_description && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <StarIcon className="h-4 w-4 text-eliza-red" />
                            <h4 className="text-sm font-semibold text-charcoal dark:text-gray-100">
                              Ideal Candidate Profile
                            </h4>
                          </div>
                          <p className="whitespace-pre-wrap pl-6 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                            {fullAnalysis.ideal_candidate_description}
                          </p>
                        </div>
                      )}

                      <div className="grid grid-cols-1 gap-4 border-t border-gray-200 pt-4 dark:border-dark-border md:grid-cols-2">
                        <div>
                          <div className="mb-1 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Created</div>
                          <div className="text-sm text-charcoal dark:text-gray-100">
                            {new Date(fullAnalysis.created_at).toLocaleString()}
                          </div>
                        </div>
                        {fullAnalysis.completed_at && (
                          <div>
                            <div className="mb-1 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Completed</div>
                            <div className="text-sm text-charcoal dark:text-gray-100">
                              {new Date(fullAnalysis.completed_at).toLocaleString()}
                            </div>
                          </div>
                        )}
                      </div>
                    </PanelBody>
                  </Panel>

                  <Panel>
                    <PanelHeader>
                      <PanelTitle>Processing Stages</PanelTitle>
                      <PanelDescription>Expand each stage to inspect intermediate outputs and scoring.</PanelDescription>
                    </PanelHeader>
                    <PanelBody className="pt-2">
                      <Accordion type="multiple" value={openStages} onValueChange={setOpenStages}>
                        <AccordionItem value="stage-1-2-diagnostic" id="stage-1-2-diagnostic">
                          <AccordionTrigger icon={<ChartBarIcon className="h-5 w-5 text-eliza-red" />}>
                            Stage 1-2: Diagnostic Analysis
                          </AccordionTrigger>
                          <AccordionContent>
                            {fullAnalysis.diagnostic_report ? (
                              <div className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-dark-border/50 dark:bg-dark-surface-2">
                                <div>
                                  <h4 className="mb-2 text-sm font-semibold text-charcoal dark:text-gray-100">Role Competencies</h4>
                                  {fullAnalysis.diagnostic_report.ml_competencies && (
                                    <div className="space-y-2">
                                      {Object.entries(fullAnalysis.diagnostic_report.ml_competencies).map(([key, value]) => (
                                        <div key={key} className="text-sm text-gray-600 dark:text-gray-300">
                                          <span className="font-medium text-charcoal dark:text-gray-100">{key}:</span>{' '}
                                          <span>{JSON.stringify(value)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                {fullAnalysis.diagnostic_report.attribute_weights && (
                                  <div>
                                    <h4 className="mb-2 text-sm font-semibold text-charcoal dark:text-gray-100">Attribute Weights</h4>
                                    <div className="space-y-2">
                                      {fullAnalysis.diagnostic_report.attribute_weights.map((attr: any) => (
                                        <div key={attr.attribute} className="flex items-center gap-2">
                                          <span className="w-32 text-sm text-gray-500 dark:text-gray-400">{attr.attribute}</span>
                                          <div className="h-2 flex-1 rounded-full bg-gray-200 dark:bg-dark-border/70">
                                            <div
                                              className="h-2 rounded-full bg-eliza-red"
                                              style={{ width: `${clamp(attr.weight * 100, 0, 100)}%` }}
                                            />
                                          </div>
                                          <span className="text-sm font-medium text-charcoal dark:text-gray-100">
                                            {(attr.weight * 100).toFixed(0)}%
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              renderStagePlaceholder(fullAnalysis.status, {
                                processing: 'Diagnostic analysis is still running.',
                                failed: 'The diagnostic analysis stage did not complete.',
                                empty: 'No diagnostic data is available for this run.',
                              })
                            )}
                          </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="stage-3-baseline-profile" id="stage-3-baseline-profile">
                          <AccordionTrigger icon={<UserGroupIcon className="h-5 w-5 text-eliza-red" />}>
                            Stage 3: Baseline Profile Analysis
                          </AccordionTrigger>
                          <AccordionContent>
                            {fullAnalysis.baseline_profile ? (
                              <div className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-dark-border/50 dark:bg-dark-surface-2">
                                <div>
                                  <h4 className="mb-2 text-sm font-semibold text-charcoal dark:text-gray-100">Profile Summary</h4>
                                  <pre className="overflow-x-auto rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-600 dark:border-dark-border/50 dark:bg-dark-surface dark:text-gray-300">
                                    {JSON.stringify(fullAnalysis.baseline_profile, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            ) : (
                              renderStagePlaceholder(fullAnalysis.status, {
                                processing: 'Baseline profile analysis is still running.',
                                failed: 'The baseline profile stage did not complete.',
                                empty: 'No baseline profile data is available for this run.',
                              })
                            )}
                          </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="stage-4-applicant-scoring" id="stage-4-applicant-scoring">
                          <AccordionTrigger icon={<StarIcon className="h-5 w-5 text-eliza-red" />}>
                            {`Stage 4: Applicant Scoring${fullAnalysis.applicant_results ? ` (${fullAnalysis.applicant_results.length} candidates)` : ''}`}
                          </AccordionTrigger>
                          <AccordionContent>
                            {fullAnalysis.applicant_results && fullAnalysis.applicant_results.length > 0 ? (
                              <div className="space-y-3">
                                {fullAnalysis.applicant_results.map((candidate) => (
                                  <Panel key={candidate.candidate_id}>
                                    <PanelBody className="space-y-3 p-4">
                                      <div className="flex items-center justify-between">
                                        <div className="font-medium text-charcoal dark:text-gray-100">
                                          {candidate.full_name || candidate.candidate_id}
                                        </div>
                                        <div className="text-lg font-bold text-eliza-red">
                                          {formatScorePercent(candidate.overall_score, 1)}
                                        </div>
                                      </div>
                                      {candidate.dimensions && candidate.dimensions.length > 0 && (
                                        <div className="space-y-2">
                                          {candidate.dimensions.map((dim) => (
                                            <div key={dim.name}>
                                              <div className="mb-1 flex items-center justify-between">
                                                <span className="text-xs text-gray-500 dark:text-gray-400">{dim.name}</span>
                                                <span className="text-xs font-medium text-charcoal dark:text-gray-100">
                                                  {formatScorePercent(dim.score)}
                                                </span>
                                              </div>
                                              <div className="mb-1 h-1.5 rounded-full bg-gray-200 dark:bg-dark-border/70">
                                                <div
                                                  className="h-1.5 rounded-full bg-eliza-red"
                                                  style={{ width: formatScorePercent(dim.score) }}
                                                />
                                              </div>
                                              {dim.rationale && (
                                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{dim.rationale}</p>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </PanelBody>
                                  </Panel>
                                ))}
                              </div>
                            ) : (
                              renderStagePlaceholder(fullAnalysis.status, {
                                processing: 'Applicant scoring is still running.',
                                failed: 'Applicant scoring did not complete.',
                                empty: 'No applicant scoring results are available.',
                              })
                            )}
                          </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="stage-5-6-market-search" id="stage-5-6-market-search">
                          <AccordionTrigger icon={<MagnifyingGlassIcon className="h-5 w-5 text-eliza-red" />}>
                            {`Stage 5-6: Market Search & Scoring${fullAnalysis.market_results ? ` (${fullAnalysis.market_results.length} candidates)` : ''}`}
                          </AccordionTrigger>
                          <AccordionContent>
                            {fullAnalysis.market_results && fullAnalysis.market_results.length > 0 ? (
                              <div className="space-y-3">
                                {fullAnalysis.market_results.slice(0, 10).map((candidate) => (
                                  <Panel key={candidate.candidate_id}>
                                    <PanelBody className="flex items-center justify-between p-4">
                                      <div className="font-medium text-charcoal dark:text-gray-100">
                                        {candidate.full_name || candidate.candidate_id}
                                      </div>
                                      <div className="text-lg font-bold text-eliza-red">
                                        {formatScorePercent(candidate.overall_score, 1)}
                                      </div>
                                    </PanelBody>
                                  </Panel>
                                ))}
                                {fullAnalysis.market_results.length > 10 && (
                                  <p className="pt-2 text-center text-sm text-gray-500 dark:text-gray-400">
                                    + {fullAnalysis.market_results.length - 10} more candidates
                                  </p>
                                )}
                              </div>
                            ) : (
                              renderStagePlaceholder(fullAnalysis.status, {
                                processing: 'Market search and scoring are still running.',
                                failed: 'Market search and scoring did not complete.',
                                empty: 'No market results are available for this run.',
                              })
                            )}
                          </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="stage-7-synthesis" id="stage-7-synthesis">
                          <AccordionTrigger icon={<SparklesIcon className="h-5 w-5 text-eliza-red" />}>
                            Stage 7: Synthesis & Insights
                          </AccordionTrigger>
                          <AccordionContent>
                            {fullAnalysis.synthesis ? (
                              <div className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-dark-border/50 dark:bg-dark-surface-2">
                                <div>
                                  <h4 className="mb-2 text-sm font-semibold text-charcoal dark:text-gray-100">Analysis Summary</h4>
                                  <pre className="overflow-x-auto rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-600 dark:border-dark-border/50 dark:bg-dark-surface dark:text-gray-300">
                                    {JSON.stringify(fullAnalysis.synthesis, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            ) : (
                              renderStagePlaceholder(fullAnalysis.status, {
                                processing: 'Synthesis is still running.',
                                failed: 'Synthesis did not complete.',
                                empty: 'No synthesis data is available for this run.',
                              })
                            )}
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    </PanelBody>
                  </Panel>

                  {/* Error State */}
                  {fullAnalysis.status === 'failed' && fullAnalysis.error_message && (
                    <Alert variant="error" title="Analysis Failed">
                      {fullAnalysis.error_message}
                    </Alert>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex h-full items-center justify-center p-6">
                <Panel className="w-full max-w-lg">
                  <PanelBody className="flex flex-col items-center justify-center py-16 text-center">
                    <DocumentTextIcon className="mb-4 h-12 w-12 text-gray-400 dark:text-gray-500" />
                    <h3 className="mb-2 text-sm font-medium text-charcoal dark:text-gray-100">
                      Select an analysis to view details
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Choose a run from the history list to inspect its summary, configuration, and staged outputs.
                    </p>
                  </PanelBody>
                </Panel>
              </div>
            )}
          </div>
        </div>

        {/* Job Description Modal */}
        {fullAnalysis?.job_description && (
          <Modal open={showJobDescriptionModal} onClose={() => setShowJobDescriptionModal(false)}>
            <ModalContent size="full" className="max-h-[80vh] overflow-hidden">
              <ModalHeader>
                <div className="flex items-start gap-3">
                  <BriefcaseIcon className="h-5 w-5 text-eliza-red" />
                  <div>
                    <ModalTitle>Job Description</ModalTitle>
                    <ModalDescription>
                      Original role brief used to generate this analysis.
                    </ModalDescription>
                  </div>
                </div>
              </ModalHeader>
              <ModalBody className="max-h-[calc(80vh-88px)] overflow-y-auto pt-4">
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-charcoal dark:text-gray-100">
                  {fullAnalysis.job_description}
                </p>
              </ModalBody>
            </ModalContent>
          </Modal>
        )}
      </PageBody>
    </Page>
  );
};
